import React from "react";
import { Link, NavLink } from "react-router-dom";
import { Coffee, Globe, Calculator, BookOpen, Sparkles, LogIn, LogOut, Languages, Menu, X, GraduationCap, Library, User, FileText, Shield, Trophy, Medal, MessageSquare, Building2 } from "lucide-react";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth";
import { useAdmin } from "@/hooks/useAdmin";
import { Button } from "@/components/ui/button";
import NotificationsBell from "@/components/NotificationsBell";

const NavItem = ({ to, icon: Icon, label, testId }) => (
  <NavLink
    to={to}
    data-testid={testId}
    className={({ isActive }) =>
      `flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs whitespace-nowrap transition-all ${
        isActive
          ? "bg-[#2A2421] text-[#F9F6F0]"
          : "text-[#2A2421] hover:bg-[#2A2421]/5"
      }`
    }
  >
    <Icon className="w-3.5 h-3.5" />
    <span>{label}</span>
  </NavLink>
);

export default function Navbar() {
  const { t, lang, toggle, isRTL } = useI18n();
  const { user, logout } = useAuth();
  const isAdmin = useAdmin();
  const [open, setOpen] = React.useState(false);
  const closeMenu = React.useCallback(() => setOpen(false), []);

  return (
    <header className="glass sticky top-0 z-40">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-6 h-14 flex items-center justify-between gap-3">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2 shrink-0">
          <div className="w-7 h-7 rounded-full bg-[#C86240] flex items-center justify-center">
            <Coffee className="w-3.5 h-3.5 text-white" />
          </div>
          <div className="leading-tight">
            <div className={`text-sm font-semibold ${isRTL ? "font-serif-ar" : "font-display"}`}>{t.appName}</div>
            <div className="text-[9px] uppercase tracking-widest text-[#5C544F] hidden xl:block">{t.tagline}</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-0.5 flex-1 justify-center" data-testid="primary-nav">
          <NavItem to="/" icon={Coffee} label={t.nav.home} testId="nav-home" />
          <NavItem to="/journey" icon={BookOpen} label={t.nav.journey} testId="nav-journey" />
          <NavItem to="/encyclopedia" icon={Library} label={t.nav.encyclopedia} testId="nav-encyclopedia" />
          <NavItem to="/academy" icon={GraduationCap} label={t.nav.academy} testId="nav-academy" />
          <NavItem to="/countries" icon={Globe} label={t.nav.countries} testId="nav-countries" />
          <NavItem to="/calculator" icon={Calculator} label={isRTL ? "الريشيو" : "Ratio"} testId="nav-calculator" />
          <NavItem to="/challenge" icon={Trophy} label={isRTL ? "التحدي" : "Challenge"} testId="nav-challenge" />
          <NavItem to="/community" icon={MessageSquare} label={isRTL ? "المجتمع" : "Community"} testId="nav-community" />
          {user && <NavItem to="/achievements" icon={Medal} label={isRTL ? "شاراتي" : "Badges"} testId="nav-achievements" />}
          <NavItem to="/recipes" icon={BookOpen} label={t.nav.recipes} testId="nav-recipes" />
          <NavItem to="/blog" icon={FileText} label={t.nav.blog} testId="nav-blog" />
          <NavItem to="/assistant" icon={Sparkles} label={isRTL ? "الذكي" : "AI"} testId="nav-assistant" />
          <NavItem to="/about" icon={User} label={isRTL ? "عني" : "About"} testId="nav-about" />
          {user && <NavItem to="/my-cafe" icon={Building2} label={isRTL ? "مقهاي" : "My Cafe"} testId="nav-my-cafe" />}
          {isAdmin && (
            <NavItem to="/admin" icon={Shield} label={isRTL ? "الإدارة" : "Admin"} testId="nav-admin" />
          )}
        </nav>

        <div className="flex items-center gap-1.5 shrink-0">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="hidden sm:flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest border border-[#2A2421]/15 hover:bg-[#2A2421]/5"
          >
            <Languages className="w-3 h-3" />
            {lang === "ar" ? "EN" : "ع"}
          </button>

          {user ? (
            <div className="flex items-center gap-1.5">
              <NotificationsBell />
              <Link to="/mine" data-testid="my-recipes-link" className="hidden xl:block text-xs text-[#2A2421] hover:underline">
                {t.nav.mine}
              </Link>
              {user.picture && (
                <img src={user.picture} alt="" className="w-7 h-7 rounded-full border border-[#2A2421]/10" />
              )}
              <Button data-testid="logout-btn" onClick={logout} variant="ghost" size="sm" className="text-[#2A2421] h-7 px-2">
                <LogOut className="w-3.5 h-3.5" />
              </Button>
            </div>
          ) : (
            <Link to="/login" data-testid="login-link">
              <Button size="sm" className="bg-[#2A2421] hover:bg-[#2A2421]/90 text-[#F9F6F0] rounded-full h-7 px-3 text-xs">
                <LogIn className="w-3.5 h-3.5 me-1" />
                {t.nav.login}
              </Button>
            </Link>
          )}

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 rounded-md hover:bg-[#2A2421]/5"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#2A2421]/10 bg-[#F9F6F0]" data-testid="mobile-nav">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1" onClick={closeMenu}>
            <NavItem to="/" icon={Coffee} label={t.nav.home} testId="nav-home-mobile" />
            <NavItem to="/journey" icon={BookOpen} label={t.nav.journey} testId="nav-journey-mobile" />
            <NavItem to="/encyclopedia" icon={Library} label={t.nav.encyclopedia} testId="nav-encyclopedia-mobile" />
            <NavItem to="/academy" icon={GraduationCap} label={t.nav.academy} testId="nav-academy-mobile" />
            <NavItem to="/countries" icon={Globe} label={t.nav.countries} testId="nav-countries-mobile" />
            <NavItem to="/calculator" icon={Calculator} label={t.nav.calculator} testId="nav-calculator-mobile" />
            <NavItem to="/challenge" icon={Trophy} label={isRTL ? "التحدي" : "Challenge"} testId="nav-challenge-mobile" />
            <NavItem to="/community" icon={MessageSquare} label={isRTL ? "المجتمع" : "Community"} testId="nav-community-mobile" />
            {user && <NavItem to="/achievements" icon={Medal} label={isRTL ? "إنجازاتي" : "Badges"} testId="nav-achievements-mobile" />}
            <NavItem to="/recipes" icon={BookOpen} label={t.nav.recipes} testId="nav-recipes-mobile" />
            <NavItem to="/blog" icon={FileText} label={t.nav.blog} testId="nav-blog-mobile" />
            <NavItem to="/assistant" icon={Sparkles} label={t.nav.assistant} testId="nav-assistant-mobile" />
            <NavItem to="/about" icon={User} label={t.nav.about} testId="nav-about-mobile" />
            {user && <NavItem to="/my-cafe" icon={Building2} label={isRTL ? "مقهاي" : "My Cafe"} testId="nav-my-cafe-mobile" />}
            {isAdmin && (
              <NavItem to="/admin" icon={Shield} label={isRTL ? "الإدارة" : "Admin"} testId="nav-admin-mobile" />
            )}
            {user && <NavItem to="/mine" icon={BookOpen} label={t.nav.mine} testId="nav-mine-mobile" />}
            <button onClick={toggle} className="mt-2 text-xs uppercase tracking-widest text-[#5C544F] flex items-center gap-1">
              <Languages className="w-3.5 h-3.5" /> {lang === "ar" ? "English" : "العربية"}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
